"use client";

import { useCallback, useMemo, useRef } from "react";

/**
 * Hands a dropdown's focus back to its trigger on close, without leaving a focus
 * ring behind on a mouse interaction.
 *
 * Radix returns focus to the trigger when the menu closes, which is what a
 * keyboard user needs, and Chrome paints `:focus-visible` on that programmatic
 * restore whether or not a keyboard was involved -- so clicking the account tile
 * and clicking away again left a ring on it. Measured three ways of performing
 * the restore (plain, blurring first, and the spec's `focusVisible: false`) and
 * Chrome rings on all of them; the ring cannot be separated from the restore.
 *
 * So the restore always happens and the ring is muted instead, for a pointer
 * dismissal only. Keyboard dismissals are left entirely to Radix and keep their
 * ring, which is the one case a ring is for. What this replaces -- skipping the
 * restore whenever the menu had been *opened* with a pointer -- dropped focus on
 * the body, losing a keyboard user's place after the ordinary act of opening a
 * menu with a tap and dismissing it with Escape.
 */
export function useMenuFocusReturn() {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const dismissedByKeyboard = useRef(false);

  const noteKeyboard = useCallback(() => {
    dismissedByKeyboard.current = true;
  }, []);

  const notePointer = useCallback(() => {
    dismissedByKeyboard.current = false;
  }, []);

  const onCloseAutoFocus = useCallback((event: Event) => {
    const trigger = triggerRef.current;
    if (dismissedByKeyboard.current || !trigger?.isConnected) {
      return;
    }

    event.preventDefault();
    muteFocusRing(trigger);
    trigger.focus({ preventScroll: true });
  }, []);

  return useMemo(
    () => ({
      /** Spread onto `DropdownMenuTrigger`. */
      triggerProps: {
        ref: triggerRef,
        onPointerDown: notePointer,
        onKeyDown: noteKeyboard,
      },
      /** Spread onto `DropdownMenuContent`. */
      contentProps: {
        onCloseAutoFocus,
        onKeyDown: noteKeyboard,
        onPointerDown: notePointer,
        onPointerDownOutside: notePointer,
      },
    }),
    [noteKeyboard, notePointer, onCloseAutoFocus],
  );
}

/**
 * Mutes the ring for this restore alone: the trigger gets its own styling back
 * the moment it loses focus or sees a key, so a keyboard user who tabs away and
 * back, or starts navigating from it, still gets an indicator.
 *
 * Inline rather than a class because it has to beat both kinds of ring these
 * triggers paint -- Chrome's outline on the plain account tile, Tailwind's ring
 * shadow on the bell -- whatever the stylesheet says.
 */
function muteFocusRing(trigger: HTMLElement) {
  const { outline, boxShadow } = trigger.style;
  trigger.style.outline = "none";
  trigger.style.boxShadow = "none";

  const restore = () => {
    trigger.style.outline = outline;
    trigger.style.boxShadow = boxShadow;
    trigger.removeEventListener("blur", restore);
    trigger.removeEventListener("keydown", restore);
  };

  trigger.addEventListener("blur", restore);
  trigger.addEventListener("keydown", restore);
}
